import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { PageHeader } from '@/components/PageHeader'
import { ViewTabs, type ViewTab } from '@/components/ViewTabs'
import { fetchPeople, type Person } from '@/features/people/api'
import { fetchDivisions, fetchProjects } from '@/features/portfolio/api'
import {
  addTeamMember,
  createCannedResponse,
  createRepository,
  createTeam,
  deleteCannedResponse,
  deleteRepository,
  deleteTeam,
  fetchCannedResponses,
  fetchRepositories,
  fetchReleaseNotes,
  fetchTeams,
  fetchWorkflowDefaults,
  removeTeamMember,
  setWorkflowDefaults,
  rotateRepositorySecret,
  updateCannedResponse,
  type CannedResponse,
  type DivisionDefaults,
  type Repository,
  type Team,
} from '@/features/settings/api'
import { fetchWorkflows } from '@/features/workflow/api'
import { WorkflowAuthoring } from '@/features/workflow/WorkflowAuthoring'
import {
  createLabel,
  fetchLabels,
  type Label,
} from '@/features/work-items/api'
import { api, type ApiError } from '@/lib/api/client'
import type { Division, Project } from '@/types/domain'
import type { Workflow } from '@/types/workflow'

type Section = 'workflows' | 'teams' | 'labels' | 'repositories' | 'canned'

/**
 * The administrative surfaces, in one place.
 *
 * Teams, labels, repositories and canned responses were all fully built on the
 * server and had no screen at all. They sit together here rather than being
 * scattered because they share an audience — someone setting the group up,
 * not someone doing the day's work — and because four new nav entries for four
 * small lists would bury the screens people actually use.
 */
export function SettingsPage() {
  const { t } = useTranslation()
  const [section, setSection] = useState<Section>('workflows')

  const tabs: ViewTab[] = [
    { id: 'workflows', label: t('workflow.authoring'), icon: 'board' },
    { id: 'teams', label: t('settings.teams'), icon: 'people' },
    { id: 'labels', label: t('settings.labels'), icon: 'dot' },
    { id: 'repositories', label: t('settings.repositories'), icon: 'link' },
    { id: 'canned', label: t('settings.canned'), icon: 'comment' },
  ]

  return (
    <div>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <ViewTabs tabs={tabs} active={section} onSelect={(id) => setSection(id as Section)} />

      <div className="pt-5">
        {section === 'workflows' ? (
          <div className="flex flex-col gap-6">
            <WorkflowAuthoring />
            <Defaults />
          </div>
        ) : null}
        {section === 'teams' ? <Teams /> : null}
        {section === 'labels' ? <Labels /> : null}
        {section === 'repositories' ? <Repositories /> : null}
        {section === 'canned' ? <Canned /> : null}
      </div>
    </div>
  )
}

function Panel({
  children,
  empty,
  isEmpty,
}: {
  children: React.ReactNode
  empty: string
  isEmpty: boolean
}) {
  if (isEmpty) {
    return (
      <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">{empty}</p>
    )
  }

  return <ul className="border-line bg-surface divide-line divide-y rounded-md border">{children}</ul>
}

/* ── Teams ───────────────────────────────────────────────────────────────── */

function Teams() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [divisionId, setDivisionId] = useState('')

  const teams = useQuery<Team[], ApiError>({ queryKey: ['teams'], queryFn: fetchTeams, retry: false })
  const divisions = useQuery<Division[], ApiError>({ queryKey: ['divisions'], queryFn: fetchDivisions, retry: false })
  const people = useQuery<Person[], ApiError>({ queryKey: ['people'], queryFn: fetchPeople, retry: false })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['teams'] })

  const create = useMutation<Team, ApiError, void>({
    mutationFn: () => createTeam({ name: name.trim(), divisionId }),
    onSuccess: () => {
      setName('')
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteTeam(id),
    onSuccess: () => void refresh(),
  })

  const addMember = useMutation<void, ApiError, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) => addTeamMember(teamId, userId),
    onSuccess: () => void refresh(),
  })

  const dropMember = useMutation<void, ApiError, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) => removeTeamMember(teamId, userId),
    onSuccess: () => void refresh(),
  })

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() !== '' && divisionId !== '') create.mutate()
        }}
      >
        <label className="flex min-w-48 flex-1 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
            {t('settings.teamName')}
          </span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]" />
        </label>
        <label className="flex w-52 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
            {t('settings.division')}
          </span>
          <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]">
            <option value="">—</option>
            {(divisions.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <button type="submit" disabled={create.isPending} className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-40">
          <Icon name="plus" className="size-3.5" />
          {t('settings.newTeam')}
        </button>
      </form>

      {create.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{create.error.message}</p>
      ) : null}

      <Panel empty={t('settings.noTeams')} isEmpty={(teams.data ?? []).length === 0}>
        {(teams.data ?? []).map((team) => (
          <li key={team.id} className="flex flex-col gap-2 px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-ink-900 min-w-0 flex-1 truncate text-sm font-medium">{team.name}</span>
              <span className="text-ink-500 text-xs">{team.division?.name ?? '—'}</span>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('settings.deleteTeamConfirm'))) remove.mutate(team.id)
                }}
                title={t('settings.deleteTeam')}
                aria-label={t('settings.deleteTeam')}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1"
              >
                <Icon name="trash" className="size-3.5" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {(team.members ?? []).map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => dropMember.mutate({ teamId: team.id, userId: member.id })}
                  title={t('settings.deleteCanned')}
                  className="border-line text-ink-700 hover:bg-surface-2 flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]"
                >
                  <Avatar name={member.name} color={member.color} size="sm" />
                  {member.name}
                </button>
              ))}

              <select
                value=""
                aria-label={t('settings.addMember')}
                onChange={(event) => {
                  if (event.target.value !== '') {
                    addMember.mutate({ teamId: team.id, userId: event.target.value })
                  }
                }}
                className="border-line bg-surface text-ink-500 rounded border px-2 py-0.5 text-[11px]"
              >
                <option value="">{t('settings.addMember')}</option>
                {(people.data ?? [])
                  .filter((person) => !(team.members ?? []).some((m) => m.id === person.id))
                  .map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
              </select>
            </div>
          </li>
        ))}
      </Panel>
    </div>
  )
}

/* ── Labels ──────────────────────────────────────────────────────────────── */

function Labels() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')

  const labels = useQuery<Label[], ApiError>({ queryKey: ['labels'], queryFn: fetchLabels, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['labels'] })

  const create = useMutation<Label, ApiError, void>({
    mutationFn: () => createLabel(name.trim()),
    onSuccess: () => {
      setName('')
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await api.delete(`/labels/${id}`)
    },
    onSuccess: () => void refresh(),
  })

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() !== '') create.mutate()
        }}
      >
        <label className="flex min-w-48 flex-1 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
            {t('settings.labelName')}
          </span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]" />
        </label>
        <button type="submit" disabled={create.isPending} className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-40">
          <Icon name="plus" className="size-3.5" />
          {t('settings.newLabel')}
        </button>
      </form>

      {create.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{create.error.message}</p>
      ) : null}

      <Panel empty={t('settings.noLabels')} isEmpty={(labels.data ?? []).length === 0}>
        {(labels.data ?? []).map((label) => (
          <li key={label.id} className="flex items-center gap-3 px-4 py-2.5">
            <span aria-hidden="true" className="size-2 shrink-0 rounded-full" style={{ backgroundColor: label.color ?? 'var(--color-ink-500)' }} />
            <span className="text-ink-900 min-w-0 flex-1 truncate text-sm">{label.name}</span>
            <span className="text-ink-500 font-mono text-[11px] tabular-nums">
              {t('settings.usedOn', { count: label.usageCount ?? 0 })}
            </span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('settings.deleteLabelConfirm'))) remove.mutate(label.id)
              }}
              title={t('settings.deleteLabel')}
              aria-label={t('settings.deleteLabel')}
              className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1"
            >
              <Icon name="trash" className="size-3.5" />
            </button>
          </li>
        ))}
      </Panel>
    </div>
  )
}

/* ── Repositories ────────────────────────────────────────────────────────── */

function Repositories() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ provider: 'github', fullName: '', externalId: '', projectId: '' })
  const [secret, setSecret] = useState<string | null>(null)

  const repositories = useQuery<Repository[], ApiError>({ queryKey: ['repositories'], queryFn: fetchRepositories, retry: false })
  const projects = useQuery<Project[], ApiError>({ queryKey: ['projects'], queryFn: () => fetchProjects(), retry: false })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['repositories'] })

  const create = useMutation<Repository, ApiError, void>({
    mutationFn: () =>
      createRepository({
        provider: form.provider,
        externalId: form.externalId.trim(),
        fullName: form.fullName.trim(),
        projectIds: [form.projectId],
      }),
    onSuccess: () => {
      setForm({ ...form, fullName: '', externalId: '' })
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteRepository(id),
    onSuccess: () => void refresh(),
  })

  // Shown once and never stored in the client: the server hashes it away and
  // the only honest thing to do is say so at the moment it appears.
  const rotate = useMutation<{ secret: string }, ApiError, string>({
    mutationFn: (id) => rotateRepositorySecret(id),
    onSuccess: (result) => {
      setSecret(result.secret)
      void refresh()
    },
  })

  const ready = form.fullName.trim() !== '' && form.externalId.trim() !== '' && form.projectId !== ''

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) create.mutate()
        }}
      >
        <label className="flex w-28 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{t('settings.provider')}</span>
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px]">
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
          </select>
        </label>
        <label className="flex min-w-44 flex-1 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{t('settings.fullName')}</span>
          <input value={form.fullName} placeholder="awj-systems/fatwa-chatbot" onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="border-line bg-surface rounded border px-2.5 py-1.5 font-mono text-[12px] outline-none focus:border-[color:var(--color-brand-300)]" />
        </label>
        <label className="flex w-32 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{t('settings.externalId')}</span>
          <input value={form.externalId} onChange={(e) => setForm({ ...form, externalId: e.target.value })} className="border-line bg-surface rounded border px-2.5 py-1.5 font-mono text-[12px] outline-none focus:border-[color:var(--color-brand-300)]" />
        </label>
        <label className="flex w-52 flex-col gap-1">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{t('settings.linkedProjects')}</span>
          <select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px]">
            <option value="">—</option>
            {(projects.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.key}</option>)}
          </select>
        </label>
        <button type="submit" disabled={!ready || create.isPending} className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-40">
          <Icon name="plus" className="size-3.5" />
          {t('settings.newRepository')}
        </button>
      </form>

      {create.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{create.error.message}</p>
      ) : null}

      {secret === null ? null : (
        <p className="border-line bg-brand-50 rounded border px-3 py-2 font-mono text-[12px] break-all">
          {secret}
          <span className="text-ink-500 ms-2 font-sans text-[11px]">{t('settings.secretShown')}</span>
        </p>
      )}

      <Panel empty={t('settings.noRepositories')} isEmpty={(repositories.data ?? []).length === 0}>
        {(repositories.data ?? []).map((repository) => (
          <li key={repository.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{repository.provider}</span>
            <span className="text-ink-900 min-w-0 flex-1 truncate font-mono text-[12px]">{repository.fullName}</span>
            <span className="text-ink-500 font-mono text-[11px]">{repository.defaultBranch ?? '—'}</span>
            <ReleaseNotes repositoryId={repository.id} />
            <button type="button" onClick={() => rotate.mutate(repository.id)} className="text-ink-500 hover:text-ink-900 text-[11px] underline">
              {t('settings.rotate')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('settings.deleteRepositoryConfirm'))) remove.mutate(repository.id)
              }}
              title={t('settings.deleteRepository')}
              aria-label={t('settings.deleteRepository')}
              className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1"
            >
              <Icon name="trash" className="size-3.5" />
            </button>
          </li>
        ))}
      </Panel>
    </div>
  )
}

/* ── Canned responses ────────────────────────────────────────────────────── */

function Canned() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState({ title: '', body: '' })
  const [editing, setEditing] = useState<string | null>(null)

  const responses = useQuery<CannedResponse[], ApiError>({
    queryKey: ['canned-responses'],
    queryFn: fetchCannedResponses,
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['canned-responses'] })

  const create = useMutation<CannedResponse, ApiError, void>({
    mutationFn: () => createCannedResponse({ title: draft.title.trim(), body: draft.body.trim() }),
    onSuccess: () => {
      setDraft({ title: '', body: '' })
      void refresh()
    },
  })

  const save = useMutation<CannedResponse, ApiError, { id: string; title: string; body: string }>({
    mutationFn: ({ id, title, body }) => updateCannedResponse(id, { title, body }),
    onSuccess: () => {
      setEditing(null)
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteCannedResponse(id),
    onSuccess: () => void refresh(),
  })

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (draft.title.trim() !== '' && draft.body.trim() !== '') create.mutate()
        }}
      >
        <input
          value={draft.title}
          placeholder={t('settings.cannedTitle')}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
        />
        <textarea
          value={draft.body}
          rows={3}
          placeholder={t('settings.cannedBody')}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          className="border-line bg-surface resize-none rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
        />
        <button type="submit" disabled={create.isPending} className="bg-brand-600 flex items-center gap-1.5 self-start rounded px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-40">
          <Icon name="plus" className="size-3.5" />
          {t('settings.newCanned')}
        </button>
      </form>

      {create.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{create.error.message}</p>
      ) : null}

      <Panel empty={t('settings.noCanned')} isEmpty={(responses.data ?? []).length === 0}>
        {(responses.data ?? []).map((response) => (
          <li key={response.id} className="flex flex-col gap-1.5 px-4 py-3">
            {editing === response.id ? (
              <EditCanned
                response={response}
                pending={save.isPending}
                onSave={(title, body) => save.mutate({ id: response.id, title, body })}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-ink-900 min-w-0 flex-1 truncate text-sm font-medium">{response.title}</span>
                  <span className="text-ink-500 font-mono text-[10px] uppercase">{response.locale}</span>
                  <button type="button" onClick={() => setEditing(response.id)} title={t('settings.save')} aria-label={t('settings.save')} className="text-ink-500 hover:text-ink-900 rounded p-1">
                    <Icon name="pencil" className="size-3.5" />
                  </button>
                  <button type="button" onClick={() => remove.mutate(response.id)} title={t('settings.deleteCanned')} aria-label={t('settings.deleteCanned')} className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1">
                    <Icon name="trash" className="size-3.5" />
                  </button>
                </div>
                <p className="text-ink-700 text-[13px] whitespace-pre-line">{response.body}</p>
              </>
            )}
          </li>
        ))}
      </Panel>
    </div>
  )
}

function EditCanned({
  response,
  pending,
  onSave,
  onCancel,
}: {
  response: CannedResponse
  pending: boolean
  onSave: (title: string, body: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(response.title)
  const [body, setBody] = useState(response.body)

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        onSave(title.trim(), body.trim())
      }}
    >
      <input value={title} onChange={(e) => setTitle(e.target.value)} className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px]" />
      <textarea value={body} rows={3} onChange={(e) => setBody(e.target.value)} className="border-line bg-surface resize-none rounded border px-2.5 py-1.5 text-[13px]" />
      <div className="flex items-center gap-2">
        <button type="submit" disabled={pending} className="bg-brand-600 rounded px-2.5 py-1 text-[12px] text-white disabled:opacity-40">
          {t('settings.save')}
        </button>
        <button type="button" onClick={onCancel} className="text-ink-500 px-1.5 py-1 text-[12px]">
          {t('settings.cancel')}
        </button>
      </div>
    </form>
  )
}

/** What changed between two refs, assembled from the work items the commits name. */
function ReleaseNotes({ repositoryId }: { repositoryId: string }) {
  const { t } = useTranslation()
  const [range, setRange] = useState({ from: '', to: '' })
  const [open, setOpen] = useState(false)

  const notes = useMutation<
    { items: { key: string; title: string }[]; from: string; to: string },
    ApiError,
    void
  >({
    mutationFn: () => fetchReleaseNotes(repositoryId, range),
  })

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-ink-500 hover:text-ink-900 text-[11px] underline">
        {t('settings.releaseNotes')}
      </button>
    )
  }

  return (
    <span className="flex w-full flex-wrap items-center gap-1.5">
      <input
        value={range.from}
        placeholder={t('settings.from')}
        aria-label={t('settings.from')}
        onChange={(event) => setRange({ ...range, from: event.target.value })}
        className="border-line bg-surface w-28 rounded border px-2 py-1 font-mono text-[11px]"
      />
      <input
        value={range.to}
        placeholder={t('settings.to')}
        aria-label={t('settings.to')}
        onChange={(event) => setRange({ ...range, to: event.target.value })}
        className="border-line bg-surface w-28 rounded border px-2 py-1 font-mono text-[11px]"
      />
      <button
        type="button"
        onClick={() => notes.mutate()}
        disabled={range.from === '' || range.to === '' || notes.isPending}
        className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white disabled:opacity-40"
      >
        {t('settings.generate')}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-ink-500 px-1.5 py-1 text-[11px]">
        {t('settings.cancel')}
      </button>

      {notes.isError ? (
        <span className="w-full text-[11px] text-[color:var(--color-state-blocked)]">{notes.error.message}</span>
      ) : null}

      {notes.data === undefined ? null : (
        <ul className="border-line bg-surface-2 w-full rounded border p-2 text-[11px]">
          {notes.data.items.map((item) => (
            <li key={item.key} className="flex gap-2">
              <span className="text-brand-600 font-mono">{item.key}</span>
              <span className="text-ink-700 min-w-0 truncate">{item.title}</span>
            </li>
          ))}
        </ul>
      )}
    </span>
  )
}

/**
 * What a new project in each division starts with.
 *
 * Set once per division rather than per project, because the answer is a
 * process decision and not something the person opening a project should have
 * to make again each time.
 */
function Defaults() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const defaults = useQuery<DivisionDefaults[], ApiError>({
    queryKey: ['workflow-defaults'],
    queryFn: fetchWorkflowDefaults,
    retry: false,
  })

  const workflows = useQuery<Workflow[], ApiError>({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    retry: false,
  })

  const save = useMutation<
    DivisionDefaults[],
    ApiError,
    { divisionId: string; deliveryWorkflowId?: string | null; engagementWorkflowId?: string | null }
  >({
    mutationFn: ({ divisionId, ...changes }) => setWorkflowDefaults(divisionId, changes),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['workflow-defaults'] }),
  })

  const pick = (applies: string) => (workflows.data ?? []).filter((w) => w.appliesTo === applies)

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-ink-900 text-sm font-semibold">{t('settings.defaults')}</h3>

      <ul className="border-line bg-surface divide-line divide-y rounded-md border">
        {(defaults.data ?? []).map((row) => (
          <li key={row.divisionId} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="text-ink-500 w-12 shrink-0 font-mono text-[10px] tracking-widest uppercase">
              {row.divisionKey}
            </span>
            <span className="text-ink-900 min-w-0 flex-1 truncate text-[13px]">{row.divisionName}</span>

            <label className="flex items-center gap-1">
              <span className="text-ink-500 font-mono text-[10px]">{t('settings.deliveryWorkflow')}</span>
              <select
                value={row.deliveryWorkflowId ?? ''}
                onChange={(event) =>
                  save.mutate({ divisionId: row.divisionId, deliveryWorkflowId: event.target.value || null })
                }
                className="border-line bg-surface rounded border px-2 py-1 text-[12px]"
              >
                <option value="">—</option>
                {pick('work_item').map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>

            <label className="flex items-center gap-1">
              <span className="text-ink-500 font-mono text-[10px]">{t('settings.engagementWorkflow')}</span>
              <select
                value={row.engagementWorkflowId ?? ''}
                onChange={(event) =>
                  save.mutate({ divisionId: row.divisionId, engagementWorkflowId: event.target.value || null })
                }
                className="border-line bg-surface rounded border px-2 py-1 text-[12px]"
              >
                <option value="">—</option>
                {pick('project').map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
          </li>
        ))}
      </ul>

      {save.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{save.error.message}</p>
      ) : null}

      <p className="text-ink-500 text-[11px]">{t('settings.defaultsNote')}</p>
    </section>
  )
}
